import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "../../components/common/Button";
import VideoPlayer from "../../components/common/VideoPlayer";
import CreateAlbumModal from "../../components/common/CreateAlbumModal";
import UploadToAlbumModal from "../../components/common/UploadToAlbumModal";
import ConfirmModal from "../../components/common/ConfirmModal";
import EditNameModal from "../../components/common/EditNameModal";
import {
  getAlbumMedia,
  getGalleriesByEvent,
  archiveMedia,
  invalidateAlbumCache,
  updateEventGallery,
  updateGalleryAlbum,
  archiveEventGallery,
  archiveGalleryAlbum,
  updateAlbumOrder,
  setAlbumThumbnail,
  setHomeImage,
} from "../../services/galleryService";
import { downloadMediaFile } from "../../services/uploadService";
import { useUpload } from "../../context/UploadContext";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const MAX_MAIN_SCREEN_ALBUMS = 4;

const getMainScreenAlbumIds = (albums) =>
  [...albums]
    .filter((album) => album.IsMainScreen)
    .sort((left, right) => {
      const leftOrder = left.MainScreenOrder ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.MainScreenOrder ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return left.Id - right.Id;
    })
    .map((album) => album.Id);

const MainScreenAlbumIcon = ({ active }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3"
      y="4"
      width="18"
      height="12"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M8 20h8M10 16.5h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M7.5 12.5l2.2-2.4 2.1 2.1 3.2-3.1 1.5 1.4"
      stroke={active ? "currentColor" : "currentColor"}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortableAlbumCard = ({
  album, targetAlbumId, collapsedAlbums, albumMedia, loadingMedia,
  visibleCount, selectedIds, deletingIds, savingAlbumId, deletingAlbumId,
  downloadingIds, mainScreenAlbumIds, togglingMainScreenId,
  selectedCount, INITIAL_SHOW,
  albumThumbnails, togglingThumbnail,
  homeImages, togglingHomeImage,
  onToggle, onEdit, onDelete, onUpload, onSelectAll,
  onToggleSelect, onOpenLightbox, onDownload, onDeleteMedia, onShowMore,
  onToggleThumbnail, onToggleHomeImage, onToggleMainScreen,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: album.Id });

  const isMainScreen = mainScreenAlbumIds.includes(album.Id);
  const mainScreenOrder = isMainScreen ? mainScreenAlbumIds.indexOf(album.Id) + 1 : null;

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      id={`album-${album.Id}`}
      style={{
        ...styles.albumCard,
        ...(Number(album.Id) === Number(targetAlbumId) ? styles.targetAlbumCard : {}),
        ...dragStyle,
      }}
    >
      {/* Album Header */}
      <div style={styles.albumHeader} onClick={() => onToggle(album.Id)}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {/* Drag handle */}
          <span
            {...attributes}
            {...listeners}
            style={styles.dragHandle}
            onClick={(e) => e.stopPropagation()}
            title="Drag to reorder"
          >
            ⠿
          </span>
        </div>
        <div style={styles.albumLeft}>
          <div style={styles.albumNameRow}>
            <span style={styles.albumName}>{album.Name}</span>
            <button
              style={{
                ...styles.mainScreenBtn,
                ...(isMainScreen ? styles.mainScreenBtnActive : {}),
              }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleMainScreen(album);
              }}
              disabled={togglingMainScreenId === album.Id || savingAlbumId === album.Id || deletingAlbumId === album.Id}
              title={isMainScreen ? `Remove from main screen (slot ${mainScreenOrder})` : "Add to main screen"}
            >
              <MainScreenAlbumIcon active={isMainScreen} />
              <span>{togglingMainScreenId === album.Id ? "Saving..." : isMainScreen ? `Main ${mainScreenOrder}` : "Main"}</span>
            </button>
            <button
              style={styles.albumActionBtn}
              onClick={(e) => { e.stopPropagation(); onEdit(album); }}
              disabled={savingAlbumId === album.Id || deletingAlbumId === album.Id}
            >
              {savingAlbumId === album.Id ? "Saving..." : "✏"}
            </button>
            <button
              style={{ ...styles.albumActionBtn, ...styles.albumDeleteBtn }}
              onClick={(e) => { e.stopPropagation(); onDelete(album); }}
              disabled={savingAlbumId === album.Id || deletingAlbumId === album.Id}
            >
              {deletingAlbumId === album.Id ? "Deleting..." : "🗑"}
            </button>
          </div>
          <div style={styles.albumMeta}>
            <span>🖼️ {albumMedia[album.Id] != null ? albumMedia[album.Id].length : album.ImageCount} images</span>
            {album.IsPremium && <span style={styles.premiumBadge}>⭐ Premium</span>}
            {isMainScreen && <span style={styles.mainScreenTag}>Main screen #{mainScreenOrder}</span>}
            {album.IsWatermark && <span style={styles.tag}>Watermark</span>}
            {album.ExpiresAt && (
              <span style={styles.tag}>Expires: {new Date(album.ExpiresAt).toLocaleDateString()}</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {(albumMedia[album.Id] || []).length > 0 && (
            <button
              style={styles.selectAllBtn}
              onClick={(e) => { e.stopPropagation(); onSelectAll(album.Id); }}
              title="Select all in album"
            >
              ☑ Select All
            </button>
          )}
          <button
            style={styles.uploadBtn}
            onClick={(e) => { e.stopPropagation(); onUpload(album); }}
            title="Upload media to this album"
          >
            📤 Upload
          </button>
          <span style={styles.chevron}>{collapsedAlbums[album.Id] ? "▼" : "▲"}</span>
        </div>
      </div>

      {/* Album Media Grid */}
      {!collapsedAlbums[album.Id] && (
        <div style={styles.imagesGrid}>
          {loadingMedia[album.Id] ? (
            <p style={{ color: "#888", fontSize: 13, padding: "16px 0 8px" }}>Loading media…</p>
          ) : !albumMedia[album.Id] || albumMedia[album.Id].length === 0 ? (
            <p style={{ color: "#888", fontSize: 13, padding: "16px 0 8px" }}>No media in this album.</p>
          ) : (
            <>
              {albumMedia[album.Id]
                .slice(0, visibleCount[album.Id] || INITIAL_SHOW)
                .map((item, idx) => (
                  <div
                    key={item.Id}
                    style={{
                      ...styles.mediaCard,
                      outline: selectedIds[item.Id] ? "3px solid #6c63ff" : "none",
                      outlineOffset: -3,
                    }}
                    onClick={() => selectedCount > 0 ? onToggleSelect(item.Id, album.Id) : onOpenLightbox(album.Id, idx)}
                  >
                    <button
                      style={{
                        ...styles.selectBtn,
                        background: selectedIds[item.Id] ? "#6c63ff" : "rgba(0,0,0,0.45)",
                      }}
                      onClick={(e) => { e.stopPropagation(); onToggleSelect(item.Id, album.Id); }}
                    >
                      {selectedIds[item.Id] ? "✓" : ""}
                    </button>
                    <button
                      style={{ ...styles.deleteBtn, opacity: deletingIds[item.Id] ? 0.5 : 1 }}
                      title="Delete"
                      onClick={(e) => { e.stopPropagation(); onDeleteMedia(album.Id, item.Id); }}
                      disabled={deletingIds[item.Id]}
                    >
                      🗑
                    </button>
                    <button
                      style={{
                        ...styles.downloadBtn,
                        opacity: downloadingIds[item.Id] ? 0.6 : 1,
                        cursor: downloadingIds[item.Id] ? "not-allowed" : "pointer",
                      }}
                      title="Download"
                      onClick={(e) => { e.stopPropagation(); onDownload(item); }}
                      disabled={!!downloadingIds[item.Id]}
                    >
                      {downloadingIds[item.Id] ? "⏳" : "⬇"}
                    </button>
                    <button
                      style={{
                        ...styles.thumbBtn,
                        background: (albumThumbnails[album.Id] || []).includes(item.Id)
                          ? "rgba(251,191,36,0.9)"
                          : "rgba(0,0,0,0.45)",
                        opacity: togglingThumbnail[item.Id] ? 0.5 : 1,
                      }}
                      title={(albumThumbnails[album.Id] || []).includes(item.Id) ? "Remove thumbnail" : "Set as thumbnail"}
                      onClick={(e) => { e.stopPropagation(); onToggleThumbnail(album.Id, item.Id); }}
                      disabled={togglingThumbnail[item.Id]}
                    >
                      ★
                    </button>
                    <button
                      style={{
                        ...styles.homeBtn,
                        background: (homeImages[album.Id] || []).includes(item.Id)
                          ? "rgba(34,197,94,0.92)"
                          : "rgba(0,0,0,0.45)",
                        opacity: togglingHomeImage[item.Id] ? 0.5 : 1,
                      }}
                      title={(homeImages[album.Id] || []).includes(item.Id) ? "Remove home image" : "Set as home image"}
                      onClick={(e) => { e.stopPropagation(); onToggleHomeImage(album.Id, item.Id); }}
                      disabled={togglingHomeImage[item.Id]}
                    >
                      ⌂
                    </button>
                    {item.VideoURL ? (
                      <>
                        <img
                          src={item.VideoThumbnail || item.MaxCompressedUrl || item.CompressedUrl || item.Url}
                          alt={item.Name || `Video ${item.Id}`}
                          style={styles.image}
                          loading="lazy"
                        />
                        <div style={styles.playOverlay}>
                          <div style={styles.playBtn}>▶</div>
                        </div>
                      </>
                    ) : (
                      <img
                        src={item.MaxCompressedUrl || item.CompressedUrl || item.Url}
                        alt={item.Name || `Image ${item.Id}`}
                        style={styles.image}
                        loading="lazy"
                        onError={(e) => { e.target.src = item.Url; }}
                      />
                    )}
                    {item.LikesCount > 0 && (
                      <div style={styles.likesOverlay}>
                        👍 {item.LikesCount}
                      </div>
                    )}
                    {item.IsAlbumThumbnail && (
                      <div style={styles.albumThumbBadge} title="Album thumbnail">
                        🖼
                      </div>
                    )}
                    {item.IsHomeImage && (
                      <div style={styles.homeImageBadge} title="Home image">
                        ⌂
                      </div>
                    )}
                  </div>
                ))}
              {albumMedia[album.Id].length > (visibleCount[album.Id] || INITIAL_SHOW) && (
                <button style={styles.showMoreBtn} onClick={() => onShowMore(album.Id)}>
                  Show more ({albumMedia[album.Id].length - (visibleCount[album.Id] || INITIAL_SHOW)} remaining)
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const GalleryDetail = () => {
  const { eventId, roomId, galleryId } = useParams();
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const targetAlbumId = Number(new URLSearchParams(search).get("albumId")) || null;
  const didFocusTargetAlbumRef = useRef(false);
  const initialGallery = state?.gallery;

  // backPath works for both event and room flows
  const backPath =
    state?.backPath ||
    (roomId ? `/rooms/${roomId}/galleries` : `/events/${eventId}`);

  const [gallery, setGallery] = useState(initialGallery);
  const [isResolvingGallery, setIsResolvingGallery] = useState(!initialGallery);
  const [albums, setAlbums] = useState(initialGallery?.Albums || []);
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [uploadAlbum, setUploadAlbum] = useState(null);
  const [albumMedia, setAlbumMedia] = useState({});
  const [loadingMedia, setLoadingMedia] = useState({});
  const [lightbox, setLightbox] = useState(null); // { albumId, index }
  const [collapsedAlbums, setCollapsedAlbums] = useState(() => {
    // Collapse all albums except the first (or except target album if deep-linked)
    const initial = {};
    (initialGallery?.Albums || []).forEach((a, idx) => {
      if (targetAlbumId) {
        initial[a.Id] = Number(a.Id) !== Number(targetAlbumId);
      } else if (idx > 0) {
        initial[a.Id] = true;
      }
    });
    return initial;
  }); // albums manually collapsed
  const [deletingIds, setDeletingIds] = useState({}); // { [mediaId]: true } — loading state
  const [downloadingIds, setDownloadingIds] = useState({}); // { [mediaId]: true } — download in progress
  const [selectedIds, setSelectedIds] = useState({}); // { [mediaId]: albumId } — multi-select
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [visibleCount, setVisibleCount] = useState({}); // { [albumId]: number }
  const [savingGallery, setSavingGallery] = useState(false);
  const [savingAlbumId, setSavingAlbumId] = useState(null);
  const [deletingAlbumId, setDeletingAlbumId] = useState(null);
  const [deletingGallery, setDeletingGallery] = useState(false);
  const [showEditGalleryModal, setShowEditGalleryModal] = useState(false);
  const [editGalleryName, setEditGalleryName] = useState("");
  const [editAlbumTarget, setEditAlbumTarget] = useState(null);
  const [editAlbumName, setEditAlbumName] = useState("");
  const [deleteAlbumTarget, setDeleteAlbumTarget] = useState(null);
  const [showDeleteGalleryModal, setShowDeleteGalleryModal] = useState(false);
  const [reorderingAlbums, setReorderingAlbums] = useState(false);
  // albumThumbnails: { [albumId]: mediaId[] } — ordered array, index+1 = displayOrder, max 3
  const [albumThumbnails, setAlbumThumbnails] = useState({});
  const [togglingThumbnail, setTogglingThumbnail] = useState({}); // { [mediaId]: true }
  const [homeImages, setHomeImages] = useState({});
  const [togglingHomeImage, setTogglingHomeImage] = useState({}); // { [mediaId]: true }
  const [togglingMainScreenId, setTogglingMainScreenId] = useState(null);
  const INITIAL_SHOW = 20;
  const LOAD_MORE_STEP = 20;
  const mainScreenAlbumIds = getMainScreenAlbumIds(albums);

  const albumSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleAlbumDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;

    const oldIndex = albums.findIndex((a) => a.Id === active.id);
    const newIndex = albums.findIndex((a) => a.Id === over.id);
    const reordered = arrayMove(albums, oldIndex, newIndex);
    setAlbums(reordered);

    const orderData = reordered.map((a, i) => ({ id: a.Id, order: i + 1 }));
    setReorderingAlbums(true);
    try {
      await updateAlbumOrder(orderData);
    } catch {
      toast.error("Failed to save album order");
      refetchGallery();
    } finally {
      setReorderingAlbums(false);
    }
  };

  // chatRoomId: for room flow use the room's Id, for event flow use the eventId param
  // Also fallback to gallery.ChatRoomId / roomId in case navigation state is lost (e.g. page refresh)
  const chatRoomId = state?.room?.Id || state?.chatRoomId || eventId || gallery?.ChatRoomId || roomId;
  const { uploadVersion } = useUpload();

  const refetchGallery = useCallback(() => {
    if (!chatRoomId) return;
    getGalleriesByEvent(chatRoomId)
      .then((res) => {
        const targetGalleryId = Number(gallery?.Id || galleryId || 0);
        const updated = (res.data?.Result || []).find((g) => Number(g.Id) === targetGalleryId);
        if (updated) {
          setGallery(updated);
          setAlbums(updated.Albums || []);
          // Only re-fetch media for albums that are already expanded (not collapsed)
          // Bust the album media cache first so we don't get stale data after an upload
          (updated.Albums || []).forEach((a) => {
            if (!collapsedAlbums[a.Id]) {
              invalidateAlbumCache(chatRoomId, a.Id);
              fetchAlbumMedia(a.Id);
            }
          });
        }
      })
      .catch(() => {});
  }, [chatRoomId, gallery?.Id, galleryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Support direct URL open (without navigation state) by resolving gallery from params.
  useEffect(() => {
    if (gallery || !chatRoomId || !galleryId) {
      if (gallery) setIsResolvingGallery(false);
      return;
    }

    let cancelled = false;
    setIsResolvingGallery(true);

    getGalleriesByEvent(chatRoomId)
      .then((res) => {
        if (cancelled) return;
        const found = (res.data?.Result || []).find((g) => Number(g.Id) === Number(galleryId));
        if (found) {
          setGallery(found);
          setAlbums(found.Albums || []);
          setCollapsedAlbums(() => {
            const initial = {};
            (found.Albums || []).forEach((a, idx) => {
              if (targetAlbumId) {
                initial[a.Id] = Number(a.Id) !== Number(targetAlbumId);
              } else if (idx > 0) {
                initial[a.Id] = true;
              }
            });
            return initial;
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsResolvingGallery(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gallery, chatRoomId, galleryId, targetAlbumId]);

  const fetchAlbumMedia = useCallback((albumId) => {
    setLoadingMedia((prev) => ({ ...prev, [albumId]: true }));
    getAlbumMedia(chatRoomId, albumId)
      .then((res) => {
        const items = res.data?.Result || [];
        setAlbumMedia((prev) => ({ ...prev, [albumId]: items }));
        // Seed thumbnail state — ordered array sorted by DisplayOrder
        const thumbIds = items
          .filter((m) => m.IsAlbumThumbnail)
          .sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0))
          .map((m) => m.Id);
        setAlbumThumbnails((prev) => ({ ...prev, [albumId]: thumbIds }));
        const homeIds = items
          .filter((m) => m.IsHomeImage)
          .sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0))
          .map((m) => m.Id);
        setHomeImages((prev) => ({ ...prev, [albumId]: homeIds }));
      })
      .catch(() => {})
      .finally(() => {
        setLoadingMedia((prev) => ({ ...prev, [albumId]: false }));
      });
  }, [chatRoomId]);

  // Auto-load only the first album's media on mount; others are collapsed and load on expand
  useEffect(() => {
    if (!albums.length || !chatRoomId) return;
    const firstAlbumId = albums[0]?.Id;
    if (!firstAlbumId) return;
    if (!albumMedia[firstAlbumId] && !loadingMedia[firstAlbumId]) {
      fetchAlbumMedia(firstAlbumId);
    }
  }, [albums, chatRoomId, albumMedia, loadingMedia, fetchAlbumMedia]);

  // Refetch gallery + album media when any upload session completes
  useEffect(() => {
    if (uploadVersion === 0 || !chatRoomId) return;
    refetchGallery();
  }, [uploadVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset one-shot deep-link focus guard when route/target changes.
  useEffect(() => {
    didFocusTargetAlbumRef.current = false;
  }, [targetAlbumId, eventId, roomId]);

  // If opened via /...?albumId=123, expand and scroll to that album.
  useEffect(() => {
    if (!targetAlbumId || !albums.length || didFocusTargetAlbumRef.current) return;

    const target = albums.find((a) => Number(a.Id) === Number(targetAlbumId));
    if (!target) return;

    setCollapsedAlbums((prev) => ({ ...prev, [target.Id]: false }));
    if (!albumMedia[target.Id] && !loadingMedia[target.Id]) {
      fetchAlbumMedia(target.Id);
    }

    const scrollToTarget = () => {
      const el = document.getElementById(`album-${target.Id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        didFocusTargetAlbumRef.current = true;
      }
    };

    // Delay to ensure album cards are rendered before scroll.
    setTimeout(scrollToTarget, 120);
  }, [targetAlbumId, albums, albumMedia, loadingMedia, fetchAlbumMedia]);

  const toggleAlbum = (albumId) => {
    setCollapsedAlbums((prev) => {
      const willExpand = prev[albumId]; // was collapsed, now expanding
      if (willExpand && !albumMedia[albumId] && !loadingMedia[albumId]) {
        fetchAlbumMedia(albumId);
      }
      return { ...prev, [albumId]: !prev[albumId] };
    });
  };

  const openEditGalleryModal = () => {
    setEditGalleryName(gallery?.Name || "");
    setShowEditGalleryModal(true);
  };

  const handleEditGalleryName = async () => {
    const trimmed = editGalleryName.trim();
    if (!trimmed) {
      toast.warning("Gallery name is required.");
      return;
    }

    setSavingGallery(true);
    try {
      await updateEventGallery(gallery.Id, {
        EventId: Number(gallery.EventId || eventId || chatRoomId),
        Name: trimmed,
      });
      toast.success("Gallery updated successfully");
      setShowEditGalleryModal(false);
      refetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to update gallery.");
    } finally {
      setSavingGallery(false);
    }
  };

  const handleDeleteGallery = async () => {
    if (deletingGallery) return;

    setDeletingGallery(true);
    try {
      await archiveEventGallery({
        galleryId: gallery.Id,
        eventId: Number(gallery.EventId || eventId || chatRoomId),
        name: gallery.Name,
      });
      toast.success("Gallery deleted");
      navigate(backPath);
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to delete gallery.");
    } finally {
      setDeletingGallery(false);
      setShowDeleteGalleryModal(false);
    }
  };

  const openEditAlbumModal = (album) => {
    setEditAlbumTarget(album);
    setEditAlbumName(album?.Name || "");
  };

  const handleEditAlbumName = async () => {
    if (!editAlbumTarget) return;
    const trimmed = editAlbumName.trim();
    if (!trimmed) {
      toast.warning("Album name is required.");
      return;
    }

    setSavingAlbumId(editAlbumTarget.Id);
    try {
      await updateGalleryAlbum({
        AlbumId: Number(editAlbumTarget.Id),
        Name: trimmed,
      });
      toast.success("Album updated successfully");
      setEditAlbumTarget(null);
      refetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to update album.");
    } finally {
      setSavingAlbumId(null);
    }
  };

  const openDeleteAlbumModal = (album) => {
    setDeleteAlbumTarget(album);
  };

  const handleDeleteAlbum = async () => {
    if (!deleteAlbumTarget || deletingAlbumId === deleteAlbumTarget.Id) return;

    setDeletingAlbumId(deleteAlbumTarget.Id);
    try {
      await archiveGalleryAlbum({
        albumId: deleteAlbumTarget.Id,
        name: deleteAlbumTarget.Name,
      });
      toast.success("Album deleted");
      setDeleteAlbumTarget(null);
      refetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to delete album.");
    } finally {
      setDeletingAlbumId(null);
    }
  };

  const showMore = (albumId) => {
    setVisibleCount((prev) => ({
      ...prev,
      [albumId]: (prev[albumId] || INITIAL_SHOW) + LOAD_MORE_STEP,
    }));
  };

  const handleDelete = async (albumId, mediaId) => {
    if (deletingIds[mediaId]) return;
    setDeletingIds((prev) => ({ ...prev, [mediaId]: true }));
    try {
      await archiveMedia([mediaId]);
      invalidateAlbumCache(chatRoomId, albumId);
      setAlbumMedia((prev) => ({
        ...prev,
        [albumId]: (prev[albumId] || []).filter((m) => m.Id !== mediaId),
      }));
      toast.success("Media deleted");
    } catch {
      toast.error("Failed to delete media");
    } finally {
      setDeletingIds((prev) => ({ ...prev, [mediaId]: false }));
    }
  };

  const selectedCount = Object.keys(selectedIds).length;

  const handleToggleThumbnail = async (albumId, mediaId) => {
    const current = albumThumbnails[albumId] || [];
    const isCurrentlyThumb = current.includes(mediaId);

    // Enforce max 3
    if (!isCurrentlyThumb && current.length >= 3) {
      toast.warning("Maximum 3 thumbnails allowed per album.");
      return;
    }

    const displayOrder = isCurrentlyThumb ? null : current.length + 1;

    setTogglingThumbnail((prev) => ({ ...prev, [mediaId]: true }));
    try {
      await setAlbumThumbnail(mediaId, !isCurrentlyThumb, displayOrder);
      setAlbumThumbnails((prev) => {
        const arr = prev[albumId] || [];
        const next = isCurrentlyThumb
          ? arr.filter((id) => id !== mediaId)
          : [...arr, mediaId];
        return { ...prev, [albumId]: next };
      });
    } catch {
      toast.error("Failed to update thumbnail");
    } finally {
      setTogglingThumbnail((prev) => ({ ...prev, [mediaId]: false }));
    }
  };

  const handleToggleHomeImage = async (albumId, mediaId) => {
    const current = homeImages[albumId] || [];
    const isCurrentlyHomeImage = current.includes(mediaId);

    // Keep the same behavior as album thumbnails.
    if (!isCurrentlyHomeImage && current.length >= 3) {
      toast.warning("Maximum 3 home images allowed per album.");
      return;
    }

    const displayOrder = isCurrentlyHomeImage ? null : current.length + 1;

    setTogglingHomeImage((prev) => ({ ...prev, [mediaId]: true }));
    try {
      await setHomeImage(mediaId, !isCurrentlyHomeImage, displayOrder);
      setHomeImages((prev) => {
        const arr = prev[albumId] || [];
        const next = isCurrentlyHomeImage
          ? arr.filter((id) => id !== mediaId)
          : [...arr, mediaId];
        return { ...prev, [albumId]: next };
      });
    } catch {
      toast.error("Failed to update home image");
    } finally {
      setTogglingHomeImage((prev) => ({ ...prev, [mediaId]: false }));
    }
  };

  const handleToggleMainScreen = async (album) => {
    const currentIds = getMainScreenAlbumIds(albums);
    const isCurrentlyMainScreen = currentIds.includes(album.Id);

    if (!isCurrentlyMainScreen && currentIds.length >= MAX_MAIN_SCREEN_ALBUMS) {
      toast.warning(`Maximum ${MAX_MAIN_SCREEN_ALBUMS} albums allowed on the main screen.`);
      return;
    }

    const nextIds = isCurrentlyMainScreen
      ? currentIds.filter((id) => id !== album.Id)
      : [...currentIds, album.Id];

    const previousAlbums = albums;
    const nextAlbums = albums.map((item) => {
      const nextIndex = nextIds.indexOf(item.Id);
      if (nextIndex === -1) {
        if (!item.IsMainScreen && !item.MainScreenOrder) return item;
        return {
          ...item,
          IsMainScreen: false,
          MainScreenOrder: null,
        };
      }

      return {
        ...item,
        IsMainScreen: true,
        MainScreenOrder: nextIndex + 1,
      };
    });

    setTogglingMainScreenId(album.Id);
    setAlbums(nextAlbums);

    try {
      const removedIds = currentIds.filter((id) => !nextIds.includes(id));

      for (const removedId of removedIds) {
        await updateGalleryAlbum({
          AlbumId: Number(removedId),
          IsMainScreen: false,
        });
      }

      for (const [index, selectedId] of nextIds.entries()) {
        await updateGalleryAlbum({
          AlbumId: Number(selectedId),
          IsMainScreen: true,
          MainScreenOrder: index + 1,
        });
      }

      toast.success(isCurrentlyMainScreen ? "Album removed from main screen" : "Album added to main screen");
    } catch (err) {
      setAlbums(previousAlbums);
      toast.error(err.response?.data?.Message || "Failed to update main screen albums.");
    } finally {
      setTogglingMainScreenId(null);
    }
  };

  const toggleSelect = (mediaId, albumId) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (next[mediaId]) delete next[mediaId];
      else next[mediaId] = albumId;
      return next;
    });
  };

  const selectAllInAlbum = (albumId) => {
    const items = albumMedia[albumId] || [];
    const allSelected = items.every((m) => selectedIds[m.Id]);
    setSelectedIds((prev) => {
      const next = { ...prev };
      items.forEach((m) => {
        if (allSelected) delete next[m.Id];
        else next[m.Id] = albumId;
      });
      return next;
    });
  };

  const clearSelection = () => setSelectedIds({});

  const handleBulkDelete = async () => {
    const ids = Object.keys(selectedIds).map(Number);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      await archiveMedia(ids);
      const uniqueAlbumIds = [...new Set(Object.values(selectedIds))];
      uniqueAlbumIds.forEach((aId) => invalidateAlbumCache(chatRoomId, aId));
      const idsSet = new Set(ids);
      setAlbumMedia((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].filter((m) => !idsSet.has(m.Id));
        }
        return next;
      });
      toast.success(`${ids.length} item${ids.length > 1 ? "s" : ""} deleted`);
      setSelectedIds({});
    } catch {
      toast.error("Failed to delete selected media");
    } finally {
      setBulkDeleting(false);
    }
  };

  const openLightbox = (albumId, index) => setLightbox({ albumId, index });
  const closeLightbox = () => setLightbox(null);

  const lightboxMedia = lightbox ? albumMedia[lightbox.albumId] || [] : [];
  const lightboxItem = lightbox ? lightboxMedia[lightbox.index] : null;

  const goNext = useCallback(() => {
    if (!lightbox) return;
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index + 1) % lightboxMedia.length,
    }));
  }, [lightbox, lightboxMedia.length]);

  const goPrev = useCallback(() => {
    if (!lightbox) return;
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index - 1 + lightboxMedia.length) % lightboxMedia.length,
    }));
  }, [lightbox, lightboxMedia.length]);

  const getDownloadUrl = (item) => {
    if (!item) return null;
    if (item.VideoURL) return item.VideoURL;
    return item.Url || null;
  };

  const getDownloadName = (item) => {
    const source = getDownloadUrl(item) || "";
    const sourceName = source.split("?")[0].split("/").pop();
    if (sourceName) return sourceName;
    const ext = item?.VideoURL ? "mp4" : "jpg";
    return `media-${item?.Id || "file"}.${ext}`;
  };

  const downloadMedia = async (item) => {
    const itemId = item?.Id;
    if (!itemId || downloadingIds[itemId]) return;

    const url = getDownloadUrl(item);
    if (!url) {
      toast.error("Download URL not available");
      return;
    }

    setDownloadingIds((prev) => ({ ...prev, [itemId]: true }));
    try {
      await downloadMediaFile({
        url,
        fileName: getDownloadName(item),
      });
    } catch {
      toast.error("Download failed. Please make sure Client app is running.");
    } finally {
      setDownloadingIds((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleBulkDownload = async () => {
    const ids = Object.keys(selectedIds);
    if (!ids.length) return;

    setBulkDownloading(true);
    let started = 0;
    let failed = 0;

    try {
      for (const id of ids) {
        const albumId = selectedIds[id];
        const item = (albumMedia[albumId] || []).find((m) => String(m.Id) === String(id));
        if (!item) {
          failed += 1;
          continue;
        }
        try {
          const url = getDownloadUrl(item);
          if (!url) throw new Error("missing url");

          setDownloadingIds((prev) => ({ ...prev, [item.Id]: true }));
          await downloadMediaFile({
            url,
            fileName: getDownloadName(item),
          });
          started += 1;
          // Space out downloads so the browser doesn't cancel concurrent ones
          await new Promise((r) => setTimeout(r, 600));
        } catch {
          failed += 1;
        } finally {
          setDownloadingIds((prev) => ({ ...prev, [item.Id]: false }));
        }
      }

      if (started > 0) {
        toast.success(`${started} download${started > 1 ? "s" : ""} started`);
      }
      if (failed > 0) {
        toast.error(`${failed} item${failed > 1 ? "s" : ""} failed to download`);
      }
    } finally {
      setBulkDownloading(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightbox, goNext, goPrev]);

  if (!gallery) {
    return (
      <div>
        <Button variant="secondary" onClick={() => navigate(backPath)}>
          ← Back to Galleries
        </Button>
        <p style={{ color: "#888", marginTop: 20 }}>
          {isResolvingGallery ? "Loading gallery..." : "Gallery data not found."}
        </p>
      </div>
    );
  }

  return (
    <div>
      <Button
        variant="secondary"
        onClick={() => navigate(backPath)}
        style={{ marginBottom: 20 }}
      >
        ← Back to Galleries
      </Button>

      {/* Gallery Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.titleRow}>
            <h2 style={styles.title}>{gallery.Name}</h2>
            <button
              style={styles.headerActionBtn}
              onClick={openEditGalleryModal}
              disabled={savingGallery || deletingGallery}
            >
              {savingGallery ? "Saving..." : "✏"}
            </button>
            <button
              style={{ ...styles.headerActionBtn, ...styles.headerDeleteBtn }}
              onClick={() => setShowDeleteGalleryModal(true)}
              disabled={savingGallery || deletingGallery}
            >
              {deletingGallery ? "Deleting..." : "🗑"}
            </button>
          </div>
          <div style={styles.metaRow}>
            {gallery.IsPremium && <span style={styles.premiumBadge}>⭐ Premium</span>}
            {gallery.IsWatermark && <span style={styles.tag}>Watermark</span>}
            {gallery.IsMediaHandlesGallery && <span style={styles.tag}>Media Handles</span>}
            {gallery.IsUserGallery && <span style={styles.tag}>User Gallery</span>}
          </div>
          <div style={styles.counts}>
            <span>🖼️ <strong>{Object.values(albumMedia).reduce((s, arr) => s + arr.length, 0) || gallery.MediaCount}</strong> media</span>
            <span>📁 <strong>{albums.length || gallery.AlbumCount}</strong> albums</span>
          </div>
          {gallery.Package && (
            <div style={styles.packageBox}>
              <strong>{gallery.Package.Name}</strong>
              <span style={styles.packageDetail}> — {gallery.Package.Description}</span>
              <span style={styles.packagePrice}> {gallery.Package.DefaultPrice} {gallery.Package.Currency}</span>
            </div>
          )}
        </div>
      </div>

      {/* Albums */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h3 style={styles.albumsHeading}>
          Albums <span style={styles.albumCount}>({albums.length})</span>
        </h3>
        {reorderingAlbums && (
          <span style={styles.orderSavingLabel}>Saving order...</span>
        )}
        <Button onClick={() => setShowCreateAlbum(true)} style={{ padding: "6px 14px", fontSize: 13 }}>
          + New Album
        </Button>
      </div>

      {albums.length === 0 ? (
        <p style={{ color: "#888", fontSize: 14 }}>No albums in this gallery.</p>
      ) : (
        <DndContext sensors={albumSensors} collisionDetection={closestCenter} onDragEnd={handleAlbumDragEnd}>
          <SortableContext items={albums.map((a) => a.Id)} strategy={verticalListSortingStrategy}>
            <div style={styles.albumsList}>
              {albums.map((album) => (
                <SortableAlbumCard
                  key={album.Id}
                  album={album}
                  targetAlbumId={targetAlbumId}
                  collapsedAlbums={collapsedAlbums}
                  albumMedia={albumMedia}
                  loadingMedia={loadingMedia}
                  visibleCount={visibleCount}
                  selectedIds={selectedIds}
                  deletingIds={deletingIds}
                  downloadingIds={downloadingIds}
                  mainScreenAlbumIds={mainScreenAlbumIds}
                  togglingMainScreenId={togglingMainScreenId}
                  savingAlbumId={savingAlbumId}
                  deletingAlbumId={deletingAlbumId}
                  selectedCount={selectedCount}
                  INITIAL_SHOW={INITIAL_SHOW}
                  albumThumbnails={albumThumbnails}
                  togglingThumbnail={togglingThumbnail}
                  homeImages={homeImages}
                  togglingHomeImage={togglingHomeImage}
                  onToggle={toggleAlbum}
                  onEdit={openEditAlbumModal}
                  onDelete={openDeleteAlbumModal}
                  onUpload={(a) => setUploadAlbum(a)}
                  onSelectAll={selectAllInAlbum}
                  onToggleSelect={toggleSelect}
                  onOpenLightbox={openLightbox}
                  onDownload={downloadMedia}
                  onDeleteMedia={handleDelete}
                  onShowMore={showMore}
                  onToggleThumbnail={handleToggleThumbnail}
                  onToggleHomeImage={handleToggleHomeImage}
                  onToggleMainScreen={handleToggleMainScreen}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {showCreateAlbum && (
        <CreateAlbumModal
          galleryId={gallery.Id}
          galleryName={gallery.Name}
          onClose={() => setShowCreateAlbum(false)}
          onCreated={() => {
            getGalleriesByEvent(chatRoomId)
              .then((res) => {
                const updated = (res.data?.Result || []).find((g) => g.Id === gallery.Id);
                if (updated?.Albums) {
                  setAlbums(updated.Albums);
                  updated.Albums.forEach((a) => {
                    if (!albumMedia[a.Id]) fetchAlbumMedia(a.Id);
                  });
                }
              })
              .catch(() => {});
          }}
        />
      )}

      {uploadAlbum && (
        <UploadToAlbumModal
          album={uploadAlbum}
          gallery={gallery}
          chatRoomId={chatRoomId}
          onClose={() => setUploadAlbum(null)}
          onUploaded={() => {
            refetchGallery();
            setUploadAlbum(null);
          }}
        />
      )}

      {/* Bulk Delete Bar */}
      {selectedCount > 0 && (
        <div style={styles.bulkBar}>
          <span style={{ fontWeight: 600 }}>{selectedCount} selected</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={styles.bulkCancelBtn} onClick={clearSelection}>Cancel</button>
            <button
              style={styles.bulkDownloadBtn}
              onClick={handleBulkDownload}
              disabled={bulkDeleting || bulkDownloading}
            >
              {bulkDownloading ? "Downloading..." : `⬇ Download (${selectedCount})`}
            </button>
            <button
              style={styles.bulkDeleteBtn}
              onClick={handleBulkDelete}
              disabled={bulkDeleting || bulkDownloading}
            >
              {bulkDeleting ? "Deleting..." : `🗑 Delete (${selectedCount})`}
            </button>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightbox && lightboxItem && (
        <div style={styles.lbOverlay} onClick={closeLightbox}>
          <div style={styles.lbContent} onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button style={styles.lbClose} onClick={closeLightbox}>✕</button>

            {/* Download */}
            <button
              style={{
                ...styles.lbDownload,
                opacity: lightboxItem && downloadingIds[lightboxItem.Id] ? 0.6 : 1,
                cursor: lightboxItem && downloadingIds[lightboxItem.Id] ? "not-allowed" : "pointer",
              }}
              onClick={() => downloadMedia(lightboxItem)}
              title="Download"
              disabled={!!(lightboxItem && downloadingIds[lightboxItem.Id])}
            >
              {lightboxItem && downloadingIds[lightboxItem.Id] ? "⏳ Downloading..." : "⬇ Download"}
            </button>

            {/* Counter */}
            <div style={styles.lbCounter}>
              {lightbox.index + 1} / {lightboxMedia.length}
            </div>

            {/* Prev */}
            {lightboxMedia.length > 1 && (
              <button style={styles.lbArrowLeft} onClick={goPrev}>‹</button>
            )}

            {/* Media */}
            <div style={styles.lbMedia}>
              {lightboxItem.VideoURL ? (
                <VideoPlayer
                  key={lightboxItem.Id}
                  src={lightboxItem.Url}
                  fallbackSrc={lightboxItem.CompressedUrl || lightboxItem.VideoURL}
                  poster={lightboxItem.VideoThumbnail}
                  style={styles.lbImage}
                  autoPlay
                />
              ) : (
                <img
                  key={lightboxItem.Id}
                  src={lightboxItem.CompressedUrl || lightboxItem.Url}
                  alt={lightboxItem.Name || `Image ${lightboxItem.Id}`}
                  style={styles.lbImage}
                  onError={(e) => { e.target.src = lightboxItem.Url; }}
                />
              )}
            </div>

            {/* Next */}
            {lightboxMedia.length > 1 && (
              <button style={styles.lbArrowRight} onClick={goNext}>›</button>
            )}
          </div>
        </div>
      )}

      {showEditGalleryModal && (
        <EditNameModal
          title="Edit Gallery Name"
          label="Gallery Name"
          value={editGalleryName}
          onChange={setEditGalleryName}
          onSave={handleEditGalleryName}
          onCancel={() => !savingGallery && setShowEditGalleryModal(false)}
          loading={savingGallery}
          saveText="Save"
        />
      )}

      {showDeleteGalleryModal && (
        <ConfirmModal
          title="Delete Gallery"
          message={`Delete gallery "${gallery?.Name || ""}"?`}
          confirmText="🗑"
          danger
          loading={deletingGallery}
          onConfirm={handleDeleteGallery}
          onCancel={() => !deletingGallery && setShowDeleteGalleryModal(false)}
        />
      )}

      {editAlbumTarget && (
        <EditNameModal
          title="Edit Album Name"
          label="Album Name"
          value={editAlbumName}
          onChange={setEditAlbumName}
          onSave={handleEditAlbumName}
          onCancel={() => !savingAlbumId && setEditAlbumTarget(null)}
          loading={savingAlbumId === editAlbumTarget.Id}
          saveText="Save"
        />
      )}

      {deleteAlbumTarget && (
        <ConfirmModal
          title="Delete Album"
          message={`Delete album "${deleteAlbumTarget.Name}"?`}
          confirmText="🗑"
          danger
          loading={deletingAlbumId === deleteAlbumTarget.Id}
          onConfirm={handleDeleteAlbum}
          onCancel={() => !deletingAlbumId && setDeleteAlbumTarget(null)}
        />
      )}
    </div>
  );
};

const styles = {
  header: {
    background: "#fff",
    borderRadius: 14,
    padding: "20px 24px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    marginBottom: 28,
    maxWidth: 680,
  },
  headerLeft: {},
  titleRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" },
  title: { margin: "0 0 10px", fontSize: 22, fontWeight: 700, color: "#1e1e2d" },
  headerActionBtn: {
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 10px",
    cursor: "pointer",
  },
  headerDeleteBtn: {
    borderColor: "#fecaca",
    color: "#b91c1c",
    background: "#fff5f5",
  },
  metaRow: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 },
  counts: { display: "flex", gap: 20, fontSize: 14, color: "#555", marginBottom: 10 },
  premiumBadge: {
    fontSize: 11, fontWeight: 700,
    background: "#fef3c7", color: "#d97706",
    padding: "2px 10px", borderRadius: 20,
  },
  tag: {
    fontSize: 11, background: "#f5f5f5", color: "#888",
    padding: "2px 10px", borderRadius: 20,
  },
  packageBox: {
    marginTop: 8,
    background: "#f9f8ff",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 13,
    color: "#444",
    borderLeft: "3px solid #6c63ff",
  },
  packageDetail: { color: "#666" },
  packagePrice: { fontWeight: 700, color: "#6c63ff" },

  albumsHeading: { margin: 0, fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  albumCount: { fontSize: 14, fontWeight: 400, color: "#888" },
  orderSavingLabel: { fontSize: 12, fontWeight: 600, color: "#6c63ff" },
  albumsList: { display: "flex", flexDirection: "column", gap: 12, maxWidth: 860 },
  dragHandle: {
    cursor: "grab",
    fontSize: 18,
    color: "#bbb",
    padding: "0 4px",
    userSelect: "none",
    lineHeight: 1,
  },

  albumCard: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  albumHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 20px",
    cursor: "pointer",
    userSelect: "none",
  },
  albumLeft: { flex: 1 },
  albumNameRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" },
  albumName: { fontWeight: 700, fontSize: 15, color: "#1e1e2d", display: "block", marginBottom: 6 },
  albumActionBtn: {
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    cursor: "pointer",
  },
  mainScreenBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#475569",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 10px",
    cursor: "pointer",
    lineHeight: 1,
  },
  mainScreenBtnActive: {
    borderColor: "#2563eb",
    background: "#eff6ff",
    color: "#1d4ed8",
  },
  albumDeleteBtn: {
    borderColor: "#fecaca",
    color: "#b91c1c",
    background: "#fff5f5",
  },
  albumMeta: { display: "flex", flexWrap: "wrap", gap: 8, fontSize: 12, color: "#666" },
  mainScreenTag: {
    fontSize: 10,
    fontWeight: 700,
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "2px 8px",
    borderRadius: 20,
  },
  chevron: { fontSize: 12, color: "#888" },
  uploadBtn: {
    background: "#6c63ff",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
  },

  imagesGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "0 16px 16px",
  },
  targetAlbumCard: {
    border: "2px solid #6c63ff",
    boxShadow: "0 0 0 3px rgba(108,99,255,0.16)",
  },
  mediaCard: {
    width: 120,
    borderRadius: 8,
    overflow: "hidden",
    background: "#f0f0f0",
    position: "relative",
    cursor: "pointer",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },
  image: {
    width: "100%",
    height: 120,
    objectFit: "cover",
    display: "block",
  },
  playOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.3)",
    pointerEvents: "none",
  },
  playBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.9)",
    color: "#1e1e2d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    paddingLeft: 2,
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  },
  deleteBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  },
  downloadBtn: {
    position: "absolute",
    bottom: 4,
    right: 4,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  },
  thumbBtn: {
    position: "absolute",
    bottom: 4,
    left: 4,
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  },
  homeBtn: {
    position: "absolute",
    bottom: 4,
    left: 34,
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
  },
  likesOverlay: {
    position: "absolute",
    bottom: 4,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 20,
    padding: "2px 7px",
    zIndex: 3,
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  albumThumbBadge: {
    position: "absolute",
    top: 4,
    right: 60,
    background: "rgba(108,99,255,0.85)",
    color: "#fff",
    fontSize: 12,
    borderRadius: "50%",
    width: 22,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3,
    pointerEvents: "none",
  },
  homeImageBadge: {
    position: "absolute",
    top: 4,
    right: 34,
    background: "rgba(34,197,94,0.92)",
    color: "#fff",
    fontSize: 12,
    borderRadius: "50%",
    width: 22,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3,
    pointerEvents: "none",
  },
  selectBtn: {
    position: "absolute",
    top: 4,
    left: 4,
    color: "#fff",
    border: "2px solid #fff",
    borderRadius: "50%",
    width: 22,
    height: 22,
    fontSize: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
    lineHeight: 1,
    fontWeight: 700,
  },
  selectAllBtn: {
    background: "none",
    color: "#6c63ff",
    border: "1px solid #6c63ff",
    borderRadius: 8,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
  },
  bulkBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#fff",
    borderTop: "1px solid #e5e7eb",
    boxShadow: "0 -2px 12px rgba(0,0,0,0.1)",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    zIndex: 999,
    fontSize: 14,
  },
  bulkDeleteBtn: {
    background: "#e53935",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  bulkDownloadBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  bulkCancelBtn: {
    background: "none",
    color: "#666",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: 13,
    cursor: "pointer",
  },
  showMoreBtn: {
    width: 120,
    height: 120,
    borderRadius: 8,
    border: "2px dashed #d1d5db",
    background: "#fafafa",
    color: "#6c63ff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 10,
  },

  // Lightbox
  lbOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lbContent: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lbClose: {
    position: "absolute",
    top: 16,
    right: 24,
    background: "transparent",
    border: "none",
    color: "#fff",
    fontSize: 28,
    cursor: "pointer",
    zIndex: 1002,
    padding: "4px 10px",
    borderRadius: 8,
    lineHeight: 1,
  },
  lbDownload: {
    position: "absolute",
    top: 16,
    right: 78,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    zIndex: 1002,
    padding: "8px 12px",
    borderRadius: 8,
    lineHeight: 1,
  },
  lbCounter: {
    position: "absolute",
    top: 20,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#ccc",
    fontSize: 14,
    fontWeight: 500,
    zIndex: 1002,
  },
  lbArrowLeft: {
    position: "absolute",
    left: 16,
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    color: "#fff",
    fontSize: 48,
    cursor: "pointer",
    zIndex: 1002,
    padding: "8px 16px",
    borderRadius: 12,
    lineHeight: 1,
  },
  lbArrowRight: {
    position: "absolute",
    right: 16,
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    color: "#fff",
    fontSize: 48,
    cursor: "pointer",
    zIndex: 1002,
    padding: "8px 16px",
    borderRadius: 12,
    lineHeight: 1,
  },
  lbMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "85vw",
    maxHeight: "85vh",
  },
  lbImage: {
    maxWidth: "85vw",
    maxHeight: "85vh",
    objectFit: "contain",
    borderRadius: 8,
    display: "block",
  },
};

export default GalleryDetail;
